/**
 * Shift Reconciliation Tab
 * 
 * Handles cashier shift management, cash counts, and daily reconciliation.
 * Starting float: 300 FJD (Mon-Fri), 500 FJD (Sat-Sun)
 * Any variance requires supervisor approval.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Clock, Check, X, AlertTriangle, Calculator, 
  RefreshCw, ChevronDown, ChevronUp, Loader2, User, 
  Calendar, TrendingUp, TrendingDown, FileText, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Fiji Dollar denominations
const DENOMINATIONS = [
  { name: '100', value: 100.0, label: '100 FJD' },
  { name: '50', value: 50.0, label: '50 FJD' },
  { name: '20', value: 20.0, label: '20 FJD' },
  { name: '10', value: 10.0, label: '10 FJD' },
  { name: '5', value: 5.0, label: '5 FJD' },
  { name: '2', value: 2.0, label: '2 FJD' },
  { name: '1', value: 1.0, label: '1 FJD' },
  { name: '0.50', value: 0.50, label: '50c' },
  { name: '0.20', value: 0.20, label: '20c' },
  { name: '0.10', value: 0.10, label: '10c' },
  { name: '0.05', value: 0.05, label: '5c' },
];

// Map denomination names to form field keys
const DENOM_FIELD_MAP = {
  '100': 'denomination_100',
  '50': 'denomination_50',
  '20': 'denomination_20',
  '10': 'denomination_10',
  '5': 'denomination_5',
  '2': 'denomination_2',
  '1': 'denomination_1',
  '0.50': 'denomination_050',
  '0.20': 'denomination_020',
  '0.10': 'denomination_010',
  '0.05': 'denomination_005',
};

// Format currency with $ for admin
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0).replace('$', '$ ').trim();
};

// Calculate total from cash count
const calculateCashTotal = (counts) => {
  let total = 0;
  DENOMINATIONS.forEach(denom => {
    const field = DENOM_FIELD_MAP[denom.name];
    const count = parseInt(counts[field] || 0);
    total += count * denom.value;
  });
  return Math.round(total * 100) / 100;
};

// Initial empty cash count
const EMPTY_CASH_COUNT = {
  denomination_100: 0,
  denomination_50: 0,
  denomination_20: 0,
  denomination_10: 0,
  denomination_5: 0,
  denomination_2: 0,
  denomination_1: 0,
  denomination_050: 0,
  denomination_020: 0,
  denomination_010: 0,
  denomination_005: 0,
};

export default function ShiftReconciliationTab() {
  const [currentShift, setCurrentShift] = useState(null);
  const [hasOpenShift, setHasOpenShift] = useState(false);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [reconSummary, setReconSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showForceCloseDialog, setShowForceCloseDialog] = useState(false);
  const [expandedShift, setExpandedShift] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  
  // Form states
  const [openForm, setOpenForm] = useState({
    cashier_name: '',
    cashier_pin: '',
    notes: '',
    opening_cash_count: { ...EMPTY_CASH_COUNT }
  });
  
  const [closeForm, setCloseForm] = useState({
    supervisor_pin: '',
    variance_explanation: '',
    notes: '',
    closing_cash_count: { ...EMPTY_CASH_COUNT }
  });
  
  const [forceCloseForm, setForceCloseForm] = useState({
    supervisor_pin: '',
    closing_cash: '',
    notes: ''
  });
  
  // Fetch current shift
  const fetchCurrentShift = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/shifts/current');
      setCurrentShift(res.data.shift);
      setHasOpenShift(res.data.has_open_shift);
    } catch (err) {
      console.error('Failed to fetch current shift:', err);
    }
  }, []);
  
  // Fetch shift history
  const fetchShiftHistory = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/shifts/history?limit=50');
      setShiftHistory(res.data.shifts || []);
    } catch (err) {
      console.error('Failed to fetch shift history:', err);
    }
  }, []);
  
  // Fetch reconciliation summary
  const fetchReconSummary = useCallback(async () => {
    try {
      const res = await api.get(`/admin/finance/reconciliation/summary?period=${selectedPeriod}`);
      setReconSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch reconciliation summary:', err);
    }
  }, [selectedPeriod]);
  
  // Initial load
  useEffect(() => {
    fetchCurrentShift();
    fetchShiftHistory();
    fetchReconSummary();
  }, [fetchCurrentShift, fetchShiftHistory, fetchReconSummary]);
  
  // Refresh summary when period changes
  useEffect(() => {
    fetchReconSummary();
  }, [selectedPeriod, fetchReconSummary]);
  
  // Open a new shift
  const handleOpenShift = async () => {
    if (!openForm.cashier_pin) {
      toast.error('Please enter your cashier PIN');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/admin/finance/shifts/open', {
        cashier_name: openForm.cashier_name,
        cashier_pin: openForm.cashier_pin,
        opening_cash_count: openForm.opening_cash_count,
        notes: openForm.notes
      });
      
      toast.success('Shift opened successfully');
      setShowOpenDialog(false);
      setOpenForm({ cashier_name: '', cashier_pin: '', notes: '', opening_cash_count: { ...EMPTY_CASH_COUNT } });
      fetchCurrentShift();
      fetchShiftHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open shift');
    } finally {
      setLoading(false);
    }
  };
  
  // Close shift
  const handleCloseShift = async () => {
    if (!closeForm.supervisor_pin) {
      toast.error('Supervisor PIN is required to close shift');
      return;
    }
    
    const closingCashTotal = calculateCashTotal(closeForm.closing_cash_count);
    const expectedCash = currentShift?.expected_closing_cash || 0;
    const variance = closingCashTotal - expectedCash;
    
    // Require explanation for any variance
    if (variance !== 0 && !closeForm.variance_explanation) {
      toast.error('Please provide an explanation for the variance');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/admin/finance/shifts/close', {
        shift_id: currentShift.id,
        closing_cash_count: closeForm.closing_cash_count,
        supervisor_pin: closeForm.supervisor_pin,
        variance_explanation: closeForm.variance_explanation,
        notes: closeForm.notes
      });
      
      toast.success(`Shift closed - ${res.data.summary.status}`);
      setShowCloseDialog(false);
      setCloseForm({ supervisor_pin: '', variance_explanation: '', notes: '', closing_cash_count: { ...EMPTY_CASH_COUNT } });
      fetchCurrentShift();
      fetchShiftHistory();
      fetchReconSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close shift');
    } finally {
      setLoading(false);
    }
  };
  
  // Force close stale shift (for when previous day's shift was left open)
  const handleForceCloseShift = async () => {
    if (!forceCloseForm.supervisor_pin) {
      toast.error('Supervisor PIN is required');
      return;
    }
    
    if (!forceCloseForm.closing_cash) {
      toast.error('Enter the closing cash amount');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/admin/finance/shifts/force-close', {
        supervisor_pin: forceCloseForm.supervisor_pin,
        closing_cash: parseFloat(forceCloseForm.closing_cash),
        notes: forceCloseForm.notes || 'Stale shift - force closed'
      });
      
      toast.success(res.data.message);
      setShowForceCloseDialog(false);
      setForceCloseForm({ supervisor_pin: '', closing_cash: '', notes: '' });
      fetchCurrentShift();
      fetchShiftHistory();
      fetchReconSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force close shift');
    } finally {
      setLoading(false);
    }
  };
  
  // Refresh all data
  const handleRefresh = () => {
    fetchCurrentShift();
    fetchShiftHistory();
    fetchReconSummary();
  };
  
  // Calculate live variance for close dialog
  const closingCashTotal = calculateCashTotal(closeForm.closing_cash_count);
  const expectedClosingCash = currentShift?.expected_closing_cash || 0;
  const liveVariance = closingCashTotal - expectedClosingCash;
  
  return (
    <div className="space-y-6" data-testid="shift-reconciliation-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Shift Reconciliation</h2>
          <p className="text-muted-foreground">
            Daily cash/card reconciliation and shift management
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      {reconSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Wallet className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cash Sales</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(reconSummary.summary?.total_cash_sales)} </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Card Sales</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(reconSummary.summary?.total_card_sales)} </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  reconSummary.summary?.total_variance === 0 
                    ? 'bg-green-100 dark:bg-green-900/30' 
                    : 'bg-amber-100 dark:bg-amber-900/30'
                }`}>
                  {reconSummary.summary?.total_variance === 0 ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Variance</p>
                  <p className={`text-xl font-bold ${
                    reconSummary.summary?.total_variance === 0 
                      ? 'text-green-600' 
                      : reconSummary.summary?.total_variance > 0 
                        ? 'text-blue-600' 
                        : 'text-red-600'
                  }`}>
                    {reconSummary.summary?.total_variance > 0 ? '+' : ''}{formatCurrency(reconSummary.summary?.total_variance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calculator className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Accuracy Rate</p>
                  <p className="text-xl font-bold text-foreground">{reconSummary.summary?.accuracy_rate || 100}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Current Shift Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Current Shift
          </CardTitle>
          <CardDescription>
            Manage the active POS shift
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!hasOpenShift ? (
            <div className="text-center py-8">
              <div className="p-4 bg-muted/50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Lock className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-medium text-foreground mb-2">No Active Shift</p>
              <p className="text-muted-foreground mb-4">Open a new shift to begin accepting payments</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => setShowOpenDialog(true)} data-testid="open-shift-btn">
                  <Clock className="w-4 h-4 mr-2" />
                  Open New Shift
                </Button>
                <Button 
                  onClick={() => setShowForceCloseDialog(true)} 
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  data-testid="force-close-shift-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Force Close Stale Shift
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Use &quot;Force Close&quot; if yesterday&apos;s shift was left open
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Shift Info */}
              <div className="bg-muted/50 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{currentShift.cashier_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Started: {new Date(currentShift.opened_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">Active</span>
                  </div>
                </div>
                
                {/* Sales Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-card rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Opening Float</p>
                    <p className="text-lg font-bold text-foreground">{formatCurrency(currentShift.actual_opening_cash)} </p>
                  </div>
                  <div className="bg-card rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Cash Sales</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(currentShift.cash_sales)} </p>
                  </div>
                  <div className="bg-card rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Card Sales</p>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(currentShift.card_sales)} </p>
                  </div>
                  <div className="bg-card rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Expected Cash</p>
                    <p className="text-lg font-bold text-primary">{formatCurrency(currentShift.expected_closing_cash)} </p>
                  </div>
                </div>
              </div>
              
              {/* Close Shift Button */}
              <Button 
                onClick={() => setShowCloseDialog(true)} 
                className="w-full"
                variant="destructive"
                data-testid="close-shift-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Close Shift
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Shift History */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Shift History
              </CardTitle>
              <CardDescription>
                Past shift records and reconciliation data
              </CardDescription>
            </div>
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {shiftHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No shift history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {shiftHistory.map(shift => (
                <div 
                  key={shift.id} 
                  className="border rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                    onClick={() => setExpandedShift(expandedShift === shift.id ? null : shift.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        shift.status === 'open' ? 'bg-green-500' : 
                        shift.variance === 0 ? 'bg-green-500' : 
                        shift.variance > 0 ? 'bg-blue-500' : 'bg-red-500'
                      }`} />
                      <div>
                        <p className="font-medium text-foreground">{shift.date}</p>
                        <p className="text-sm text-muted-foreground">{shift.cashier_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium text-foreground">{formatCurrency(shift.total_sales)} </p>
                        {shift.status === 'closed' && (
                          <p className={`text-sm ${
                            shift.variance === 0 ? 'text-green-600' : 
                            shift.variance > 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            {shift.variance === 0 ? 'Balanced' : 
                             shift.variance > 0 ? `+${formatCurrency(shift.variance)}` : formatCurrency(shift.variance)}
                          </p>
                        )}
                      </div>
                      {expandedShift === shift.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {expandedShift === shift.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="p-4 border-t space-y-4">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Opening Cash</p>
                              <p className="font-medium">{formatCurrency(shift.actual_opening_cash)} </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Cash Sales</p>
                              <p className="font-medium">{formatCurrency(shift.cash_sales)} </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Card Sales</p>
                              <p className="font-medium">{formatCurrency(shift.card_sales)} </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Expected Cash</p>
                              <p className="font-medium">{formatCurrency(shift.expected_closing_cash)} </p>
                            </div>
                          </div>
                          
                          {shift.status === 'closed' && (
                            <>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Actual Cash</p>
                                  <p className="font-medium">{formatCurrency(shift.actual_closing_cash)} </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Variance</p>
                                  <p className={`font-medium ${
                                    shift.variance === 0 ? 'text-green-600' : 
                                    shift.variance > 0 ? 'text-blue-600' : 'text-red-600'
                                  }`}>
                                    {shift.variance === 0 ? 'BALANCED' : 
                                     shift.variance > 0 ? `+${formatCurrency(shift.variance)}` : formatCurrency(shift.variance)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Supervisor</p>
                                  <p className="font-medium">{shift.supervisor_name || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Closed At</p>
                                  <p className="font-medium">{new Date(shift.closed_at).toLocaleTimeString()}</p>
                                </div>
                              </div>
                              
                              {shift.variance_explanation && (
                                <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                                  <p className="text-sm font-medium text-amber-800 dark:text-amber-400">Variance Explanation:</p>
                                  <p className="text-sm text-amber-700 dark:text-amber-300">{shift.variance_explanation}</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Open Shift Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Open New Shift</DialogTitle>
            <DialogDescription>
              Count the cash drawer and enter your PIN to start a new shift
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Cashier Name (Optional)</Label>
              <Input
                placeholder="Your name"
                value={openForm.cashier_name}
                onChange={(e) => setOpenForm({ ...openForm, cashier_name: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Cashier PIN *</Label>
              <Input
                type="password"
                placeholder="Enter your 6-digit PIN"
                maxLength={6}
                value={openForm.cashier_pin}
                onChange={(e) => setOpenForm({ ...openForm, cashier_pin: e.target.value })}
                data-testid="cashier-pin-input"
              />
            </div>
            
            {/* Cash Count */}
            <div>
              <Label className="mb-2 block">Opening Cash Count (Optional)</Label>
              <div className="grid grid-cols-3 gap-2">
                {DENOMINATIONS.map(denom => {
                  const field = DENOM_FIELD_MAP[denom.name];
                  return (
                    <div key={denom.name} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-12">{denom.label}</span>
                      <Input
                        type="number"
                        min="0"
                        className="h-8 text-sm"
                        value={openForm.opening_cash_count[field] || ''}
                        onChange={(e) => setOpenForm({
                          ...openForm,
                          opening_cash_count: {
                            ...openForm.opening_cash_count,
                            [field]: parseInt(e.target.value) || 0
                          }
                        })}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Total: <span className="font-medium">{formatCurrency(calculateCashTotal(openForm.opening_cash_count))} </span>
              </p>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any notes about this shift..."
                value={openForm.notes}
                onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleOpenShift} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
              Open Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Close Shift Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Close Shift</DialogTitle>
            <DialogDescription>
              Count the cash drawer and have a supervisor verify
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Expected vs Counted */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expected Cash:</span>
                <span className="font-medium">{formatCurrency(expectedClosingCash)} </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Your Count:</span>
                <span className="font-medium">{formatCurrency(closingCashTotal)} </span>
              </div>
              <div className={`flex justify-between pt-2 border-t ${
                liveVariance === 0 ? 'text-green-600' : 
                liveVariance > 0 ? 'text-blue-600' : 'text-red-600'
              }`}>
                <span className="font-medium">Variance:</span>
                <span className="font-bold">
                  {liveVariance === 0 ? 'BALANCED' : 
                   liveVariance > 0 ? `+${formatCurrency(liveVariance)}` : formatCurrency(liveVariance)}
                </span>
              </div>
            </div>
            
            {/* Cash Count */}
            <div>
              <Label className="mb-2 block">Closing Cash Count *</Label>
              <div className="grid grid-cols-3 gap-2">
                {DENOMINATIONS.map(denom => {
                  const field = DENOM_FIELD_MAP[denom.name];
                  return (
                    <div key={denom.name} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-12">{denom.label}</span>
                      <Input
                        type="number"
                        min="0"
                        className="h-8 text-sm"
                        value={closeForm.closing_cash_count[field] || ''}
                        onChange={(e) => setCloseForm({
                          ...closeForm,
                          closing_cash_count: {
                            ...closeForm.closing_cash_count,
                            [field]: parseInt(e.target.value) || 0
                          }
                        })}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Variance Explanation (Required if variance exists) */}
            {liveVariance !== 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                <Label className="text-amber-800 dark:text-amber-400">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Variance Explanation Required *
                </Label>
                <Textarea
                  placeholder="Explain the reason for the variance..."
                  value={closeForm.variance_explanation}
                  onChange={(e) => setCloseForm({ ...closeForm, variance_explanation: e.target.value })}
                  rows={2}
                  className="mt-2"
                />
              </div>
            )}
            
            <div>
              <Label>Supervisor PIN *</Label>
              <Input
                type="password"
                placeholder="Supervisor's 6-digit PIN"
                maxLength={6}
                value={closeForm.supervisor_pin}
                onChange={(e) => setCloseForm({ ...closeForm, supervisor_pin: e.target.value })}
                data-testid="supervisor-pin-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Required to approve shift closure
              </p>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any notes about this shift..."
                value={closeForm.notes}
                onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCloseShift} disabled={loading} variant="destructive">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Close Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Force Close Stale Shift Dialog */}
      <Dialog open={showForceCloseDialog} onOpenChange={setShowForceCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Force Close Stale Shift
            </DialogTitle>
            <DialogDescription>
              Use this when a previous day&apos;s shift was left open and needs to be closed before opening a new one.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>Warning:</strong> This will close any open shift from a previous date. 
                Enter the cash that was actually in the drawer when you collected it.
              </p>
            </div>
            
            <div>
              <Label>Closing Cash Amount *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="pl-7"
                  value={forceCloseForm.closing_cash}
                  onChange={(e) => setForceCloseForm({ ...forceCloseForm, closing_cash: e.target.value })}
                  data-testid="force-close-cash-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Enter the total cash collected from yesterday&apos;s shift
              </p>
            </div>
            
            <div>
              <Label>Supervisor PIN *</Label>
              <Input
                type="password"
                placeholder="6-digit PIN"
                maxLength={6}
                value={forceCloseForm.supervisor_pin}
                onChange={(e) => setForceCloseForm({ ...forceCloseForm, supervisor_pin: e.target.value })}
                data-testid="force-close-pin-input"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Reason for force closing..."
                value={forceCloseForm.notes}
                onChange={(e) => setForceCloseForm({ ...forceCloseForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForceCloseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleForceCloseShift} 
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Force Close Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
